import { useAuth } from "@clerk/clerk-react";
import { useLocation, useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useMemo } from "react";
import { isAuthPath } from "../lib/mobile-access";
import { hasOrgAccess, useBillingStatus } from "./BillingStatusContext";

type GuardState =
  | "loading"
  | "redirect-to-sign-in"
  | "redirect-to-app"
  | "allow";

function useGuardState(): GuardState {
  const { isLoaded, isSignedIn } = useAuth();
  const location = useLocation();
  const { billingStatus, isBillingLoading } = useBillingStatus();

  const isAuthPathMatch = isAuthPath(location.pathname);
  const isPricingPath = location.pathname.startsWith("/pricing");
  const hasPaid = billingStatus?.has_active_subscription ?? false;

  const guardLoading = !isLoaded;
  const guardNotSignedIn = !isSignedIn;
  const guardNeedsBilling = !isPricingPath && isBillingLoading;
  const isUnprotectedRoute = isAuthPathMatch || isPricingPath;
  // Signed-in users (paid or free) never belong on the auth pages, and paid
  // users don't need pricing — send both into the app.
  const guardRedirectToApp = isAuthPathMatch || (isPricingPath && hasPaid);

  // Note there is no payment gate here. Every route is reachable by any
  // signed-in user: the free plan is permanent, and a spent allowance is
  // shown as an overlay on the page the user is already on rather than a
  // bounce to /pricing. The API remains the real gate.

  return useMemo(() => {
    if (guardLoading) return "loading";
    if (guardNotSignedIn)
      return isUnprotectedRoute ? "allow" : "redirect-to-sign-in";
    if (guardNeedsBilling) return "loading";
    if (guardRedirectToApp) return "redirect-to-app";
    return "allow";
  }, [
    guardLoading,
    guardNotSignedIn,
    guardNeedsBilling,
    guardRedirectToApp,
    isUnprotectedRoute,
  ]);
}

export function AuthGuard({ children }: { children: React.ReactNode }) {
  const guardState = useGuardState();
  const navigate = useNavigate();
  const location = useLocation();
  const search = useSearch({ strict: false });
  const { billingStatus } = useBillingStatus();

  const rawRedirect = (search as { redirect_url?: string }).redirect_url ?? "/";
  const redirectUrl = rawRedirect.startsWith("/") ? rawRedirect : "/";

  useEffect(() => {
    if (guardState === "redirect-to-sign-in") {
      // Keep the query string: /refine and /results can't render without it.
      navigate({
        to: "/sign-in",
        search: { redirect_url: `${location.pathname}${location.searchStr}` },
      });
    } else if (guardState === "redirect-to-app") {
      const targetUrl = hasOrgAccess(billingStatus)
        ? "/organization"
        : redirectUrl;
      // Only navigate if we're not already on the target page
      if (location.pathname !== targetUrl) {
        navigate({ to: targetUrl });
      }
    }
  }, [
    guardState,
    navigate,
    location.pathname,
    location.searchStr,
    redirectUrl,
    billingStatus,
  ]);

  if (guardState === "loading") {
    return (
      <FullScreenMessage>
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--text-primary)]" />
        <p className="mt-2 text-[var(--text-secondary)]">Loading...</p>
      </FullScreenMessage>
    );
  }

  if (guardState !== "allow") {
    return (
      <FullScreenMessage>
        <p className="text-[var(--text-secondary)]">Redirecting...</p>
      </FullScreenMessage>
    );
  }

  return <>{children}</>;
}

function FullScreenMessage({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-center min-h-screen bg-[var(--bg-default)] px-6">
      <div className="text-center max-w-md">{children}</div>
    </div>
  );
}
